import useClevertapForSrp from '@/src/hooks/analytics/clevertap/srp'
import useGetSearchQueryURL from '@/src/hooks/srp/getSearchQueryURL'
import { useThemedToast } from '@/src/hooks/ui/useThemedToast'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import { Box, Button, ClickAwayListener, Divider, IconButton } from '@mui/material'
import { useTranslations } from 'next-intl'
import React, { useEffect, useRef, useState } from 'react'
import DatePicker from './CalendarWrapper/DatePicker'
import { FromToInput } from './fromToInput'
import { useSearchInputManager } from './hooks/search-inputs-manager'
import { MultiCityRouteSummary } from './multiCityRouteSummary'
import { TravelersInput } from './TravelersInput'
import { TripTypeSelector } from './tripTypeSelector'

interface InputsContainerProps {
    jsons?: Record<string, any>
    overlayOpen: () => void
    overlayClose: () => void
    onRegisterCloseAll?: (closeAll: () => void) => void
    onRegisterOpenMulticityEditor?: (fn: () => void) => void
    srpResultsLoading: boolean
}

export const InputsContainer: React.FC<InputsContainerProps> = ({
    jsons,
    overlayOpen,
    overlayClose,
    onRegisterCloseAll,
    onRegisterOpenMulticityEditor,
    srpResultsLoading
}) => {
    const {
        getAirportsFromQuery,
        fromToInputsState,
        onAirportSelect,
        handleSwap,
        tripType,
        setTripType,
        isRoundTrip,
        range,
        rangeDispatch,
        flightClassState,
        travelersState,
        handleTravelerChange,
        handleFlightClassChange,
        canIncreaseAdults,
        canIncreaseChildren,
        canIncreaseInfants,
        canDecreaseAdults,
        canDecreaseChildren,
        canDecreaseInfants,
        updateSearchParams,
        updateMultiCitySearchParams,
        handleAutoApplyPreferences,
        multiCitySegments,
        handleMultiCitySegmentUpdate,
        handleMultiCitySwap,
        handleAddMultiCitySegment,
        handleRemoveMultiCitySegment,
        getAirportsForMultiCitySegment,
        canAddMoreSegment,
        hasDuplicateSectors
    } = useSearchInputManager(jsons, srpResultsLoading)

    const isMultiCity = tripType === 'multicity'

    const handleTripTypeChange = (nextTripType: typeof tripType) => {
        setTripType(nextTripType)
        setActiveComponent(null)
    }

    // TripTypeSelector calls onClose() after onTripTypeChange(), which resets activeComponent to null.
    // Track the previous tripType via ref and open the route summary in an effect so it fires
    // after all synchronous state updates from the selection event have settled.
    const prevTripTypeRef = useRef(tripType)
    useEffect(() => {
        const prev = prevTripTypeRef.current
        prevTripTypeRef.current = tripType
        if (prev !== 'multicity' && tripType === 'multicity') {
            setActiveComponent('multicityroute')
        }
    }, [tripType])
    // Fare-calendar inputs for the OW/RT date picker, supplied as props so the header never
    // subscribes to the results query (which would re-trigger an errored search on mount).
    const { isPerPaxPricingRequired } = useGetSearchQueryURL()
    const owRtIsPerPaxPricingEnabled = !!isPerPaxPricingRequired && !!jsons?.isPerPaxPricingEnabled

    const { showError } = useThemedToast()
    const t = useTranslations('common')
    // Define the possible active component types
    type ActiveComponent = 'triptype' | 'from' | 'to' | 'date' | 'travelers' | 'multicityroute' | null
    const [activeComponent, setActiveComponent] = useState<ActiveComponent>(null)

    // Validation state for same airport error
    const [sameAirportError, setSameAirportError] = useState(false)

    // Check if from and to airports are the same
    useEffect(() => {
        const fromValue = fromToInputsState.from.value as string
        const toValue = fromToInputsState.to.value as string

        if (fromValue && toValue) {
            // Extract airport codes from the values
            const fromCode = fromValue.split(' - ')[0]
            const toCode = toValue.split(' - ')[0]

            setSameAirportError(fromCode === toCode && fromCode !== '')
        } else {
            setSameAirportError(false)
        }
    }, [fromToInputsState.from.value, fromToInputsState.to.value])

    useEffect(() => {
        if (activeComponent) {
            overlayOpen()
        } else {
            overlayClose()
        }
    }, [activeComponent, overlayOpen, overlayClose])

    useEffect(() => {
        onRegisterCloseAll?.(() => setActiveComponent(null))
    }, [onRegisterCloseAll])

    useEffect(() => {
        onRegisterOpenMulticityEditor?.(() => setActiveComponent('multicityroute'))
    }, [onRegisterOpenMulticityEditor])

    // Close dropdown when SRP loading state changes (prevents Popper jumping to top-left after results load)
    useEffect(() => {
        setActiveComponent(null)
    }, [srpResultsLoading])

    // Centralized function to handle component activation
    const handleComponentActivation = (component: ActiveComponent) => {
        if (activeComponent === component && component !== 'from' && component !== 'to') {
            // If clicking the same component, close it (except for from/to inputs)
            setActiveComponent(null)
        } else {
            // If clicking a different component, switch to it
            setActiveComponent(component)
        }
    }

    // Function to close any active component
    const closeActiveComponent = () => {
        setActiveComponent(null)
    }
    const swapHandler = () => {
        setActiveComponent(null)
        handleSwap()
    }

    // Handle click away - this should close any active component
    const handleClickAway = (event: Event | React.SyntheticEvent) => {
        const target = event.target as HTMLElement
        if (
            target.closest('.MuiMenu-root') ||
            target.closest('.MuiModal-root') ||
            target.closest('.MuiPopper-root')
        ) {
            return
        }
        if (activeComponent) {
            setActiveComponent(null)
        }
    }

    const { triggerSrpEvent } = useClevertapForSrp()

    //HADNLE UPDATE while closing overlay
    const handleUpdate = () => {
        if (!isMultiCity && sameAirportError) {
            return
        }

        if (isMultiCity) {
            const hasEmptyFields = multiCitySegments.some(
                segment => !segment.from.airportCode || !segment.to.airportCode || !segment.date
            )
            const hasSameAirportError = multiCitySegments.some(
                segment =>
                    segment.from.airportCode &&
                    segment.to.airportCode &&
                    segment.from.airportCode === segment.to.airportCode
            )
            if (hasEmptyFields) {
                showError(t('pleaseFillAllRequiredFields'))
                setActiveComponent('multicityroute')
                return
            }
            if (hasSameAirportError) {
                showError(t('sameAirportError'))
                setActiveComponent('multicityroute')
                return
            }
            if (hasDuplicateSectors) {
                showError(t('duplicateSectorError'))
                setActiveComponent('multicityroute')
                return
            }
        }

        setActiveComponent(null)

        triggerSrpEvent('Air_UI_Action', {
            action_name: 'modify_search',
            action_type: 'BUTTON',
            action_value: 'N/A'
        })

        if (isMultiCity) {
            updateMultiCitySearchParams()
        } else {
            updateSearchParams()
        }
    }

    // Helper functions to check if a specific component is active
    const isComponentActive = (component: ActiveComponent) => activeComponent === component

    useEffect(() => {
        if (sameAirportError) {
            showError(t('sameAirportError'))
        }
    }, [sameAirportError])
    return (
        <Box sx={{ position: 'relative', zIndex: 0 }}>
            <ClickAwayListener onClickAway={handleClickAway}>
                <Box
                    data-search-container
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        overflow: 'hidden',
                        px: 1,
                        py: 1,
                        bgcolor: '#ffffff',
                        border: '1px solid rgb(229 231 235)',
                        borderRadius: '0.75rem',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                        position: 'relative',
                        zIndex: 1
                    }}
                >
                    <TripTypeSelector
                        tripType={tripType}
                        onTripTypeChange={handleTripTypeChange}
                        active={isComponentActive('triptype')}
                        onActivate={() => handleComponentActivation('triptype')}
                        onClose={closeActiveComponent}
                        attachToSearchContainer
                    />

                    {/* Keep both trip-type UIs mounted; hide inactive branch with CSS so opening
                        dropdowns or switching trip type does not mount new RTK subscribers. */}
                    <Box
                        sx={{
                            display: isMultiCity ? 'none' : 'flex',
                            alignItems: 'center',
                            flex: 1,
                            minWidth: 0,
                            pointerEvents: isMultiCity ? 'none' : 'auto'
                        }}
                    >
                        <FromToInput
                            srpResultsLoading={srpResultsLoading}
                            key={`${fromToInputsState.from.value}-from` as string}
                            action="from"
                            initialValue={fromToInputsState.from.value as string}
                            loading={fromToInputsState.from.loading}
                            optionListItems={fromToInputsState.from.options}
                            queryOptionListItems={getAirportsFromQuery}
                            onOptionSelect={onAirportSelect}
                            active={isComponentActive('from')}
                            onActivate={() => handleComponentActivation('from')}
                            onClose={closeActiveComponent}
                            hasError={sameAirportError}
                            isPopularAirports={fromToInputsState.from.isPopularAirports}
                        />

                        <IconButton sx={{ mx: 1, bgcolor: '#f3f4f6' }} size="small" onClick={swapHandler}>
                            <SwapHorizIcon />
                        </IconButton>

                        <FromToInput
                            key={`${fromToInputsState.to.value}-to` as string}
                            action="to"
                            initialValue={fromToInputsState.to.value as string}
                            loading={fromToInputsState.to.loading}
                            optionListItems={fromToInputsState.to.options}
                            queryOptionListItems={getAirportsFromQuery}
                            onOptionSelect={onAirportSelect}
                            active={isComponentActive('to')}
                            onActivate={() => handleComponentActivation('to')}
                            onClose={closeActiveComponent}
                            hasError={sameAirportError}
                            isPopularAirports={fromToInputsState.to.isPopularAirports}
                        />

                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                        <DatePicker
                            isRoundTrip={isRoundTrip}
                            range={range}
                            dateClick={rangeDispatch}
                            active={isComponentActive('date')}
                            onActivate={() => handleComponentActivation('date')}
                            onClose={closeActiveComponent}
                            jsons={jsons}
                            isPerPaxPricingEnabled={owRtIsPerPaxPricingEnabled}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: isMultiCity ? 'block' : 'none',
                            flex: 1,
                            minWidth: 0,
                            pointerEvents: isMultiCity ? 'auto' : 'none'
                        }}
                    >
                        <MultiCityRouteSummary
                            isVisible={isMultiCity}
                            active={isComponentActive('multicityroute')}
                            onActivate={() => handleComponentActivation('multicityroute')}
                            onClose={closeActiveComponent}
                            segments={multiCitySegments}
                            canAddMoreSegment={canAddMoreSegment}
                            handleMultiCitySegmentUpdate={handleMultiCitySegmentUpdate}
                            handleMultiCitySwap={handleMultiCitySwap}
                            handleAddMultiCitySegment={handleAddMultiCitySegment}
                            handleRemoveMultiCitySegment={handleRemoveMultiCitySegment}
                            getAirportsForMultiCitySegment={getAirportsForMultiCitySegment}
                        />
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            minWidth: 165,
                            maxWidth: 165,
                            overflow: 'hidden'
                        }}
                    >
                        <TravelersInput
                            adults={travelersState.adults}
                            childs={travelersState.childs}
                            infants={travelersState.infants}
                            flightClass={flightClassState as string}
                            onTravelerChange={handleTravelerChange}
                            onFlightClassChange={handleFlightClassChange}
                            canIncreaseAdults={canIncreaseAdults(travelersState)}
                            canIncreaseChildren={canIncreaseChildren(travelersState)}
                            canIncreaseInfants={canIncreaseInfants(travelersState)}
                            canDecreaseAdults={canDecreaseAdults(travelersState)}
                            canDecreaseChildren={canDecreaseChildren(travelersState)}
                            canDecreaseInfants={canDecreaseInfants(travelersState)}
                            active={isComponentActive('travelers')}
                            onActivate={() => handleComponentActivation('travelers')}
                            onClose={closeActiveComponent}
                            attachToSearchContainer
                        />
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                    {/* <AutoApplyPreferencesToggle onChange={handleAutoApplyPreferences} /> */}
                    {/* Update Button */}
                    <Box sx={{ px: 2 }}>

                        <Button
                            disabled={srpResultsLoading}
                            variant="contained"
                            color="primary"
                            onClick={handleUpdate}
                        >
                            {t('update')}
                        </Button>
                    </Box>
                </Box>
            </ClickAwayListener>
        </Box>
    )
}
